import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export function Modal({
  isOpen,
  setIsOpen,
  children,
  title,
  headerClassName,
  className,
  stableHeight,
  isPrintable,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  headerClassName?: string;
  className?: string;
  stableHeight?: boolean;
  isPrintable?: boolean;
}) {
  const ifPrintable = (printClassNames: string) =>
    isPrintable ? ` ${printClassNames}` : '';

  return (
    <Dialog
      open={isOpen}
      onClose={setIsOpen}
      className='relative z-modalPanel'
      data-printable-dialog={isPrintable || undefined}
    >
      <DialogBackdrop
        transition
        className={`fixed inset-0 bg-gray-500 bg-opacity-40 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in${ifPrintable(
          'print:hidden'
        )}`}
      />

      <div
        className={`fixed inset-0 z-10 w-screen overflow-y-auto${ifPrintable(
          'print:static print:w-auto print:overflow-visible'
        )}`}
      >
        <div
          className={`flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0${ifPrintable(
            'print:block'
          )}`}
        >
          <DialogPanel
            transition
            className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all
    data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200
    data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-fit sm:min-w-fit sm:max-w-full
    data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 ${
      stableHeight ? 'flex flex-col ' : ''
    }${className || ''}${ifPrintable(
              'print:block print:h-auto print:w-auto print:min-w-0 print:overflow-visible print:transform-none print:shadow-none'
            )}`}
          >
            {title ? (
              <div
                className={`flex items-center justify-between px-4 pt-5 pb-4 sm:px-6 sm:pt-6 sm:pb-4 ${
                  headerClassName || ''
                }`}
              >
                <DialogTitle className='text-lg font-medium leading-6 text-gray-900'>
                  {title}
                </DialogTitle>
                <button
                  type='button'
                  onClick={() => setIsOpen(false)}
                  className={`rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600${ifPrintable(
                    'print:hidden'
                  )}`}
                >
                  <span className='sr-only'>Close</span>
                  <XMarkIcon aria-hidden='true' className='size-6' />
                </button>
              </div>
            ) : (
              <div
                className={`absolute top-0 right-0 hidden pt-4 pr-4 sm:block${ifPrintable(
                  'print:hidden'
                )}`}
              >
                <button
                  type='button'
                  onClick={() => setIsOpen(false)}
                  className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600'
                >
                  <span className='sr-only'>Close</span>
                  <XMarkIcon aria-hidden='true' className='size-6' />
                </button>
              </div>
            )}
            <div
              className={`${
                stableHeight ? 'flex min-h-0 flex-1 flex-col ' : ''
              }px-4 pt-4 pb-4 sm:px-6 sm:pt-6 sm:pb-6${ifPrintable(
                'print:block'
              )}`}
            >
              {children}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
